import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';
import ImpersonateFrame from '../../components/ImpersonateFrame';

const GerenciarUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  
  // Novos estados para as funcionalidades
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changingPasswordUser, setChangingPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [impersonateToken, setImpersonateToken] = useState(null);
  const [impersonatedUser, setImpersonatedUser] = useState(null);

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/usuarios?page=${page}&search=${search}`);
      setUsuarios(response.data.usuarios);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, [page, search]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
  };

  const handleSaveUser = async () => {
    try {
      await api.put(`/admin/usuarios/${editingUser.id}`, {
        nome: editingUser.nome,
        email: editingUser.email,
        nivel: editingUser.nivel
      });
      
      setEditingUser(null);
      fetchUsuarios();
      alert('Usuário atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      alert('Erro ao atualizar usuário');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await api.delete(`/admin/usuarios/${userId}`);
        fetchUsuarios();
        alert('Usuário excluído com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir usuário:', error);
        alert('Erro ao excluir usuário');
      }
    }
  };

  const handleLogoutUser = async (userId) => {
    if (window.confirm('Tem certeza que deseja deslogar este usuário?')) {
      try {
        await api.post(`/admin/usuarios/${userId}/logout`);
        alert('Usuário deslogado com sucesso!');
      } catch (error) {
        console.error('Erro ao deslogar usuário:', error);
        alert('Erro ao deslogar usuário');
      }
    }
  };

  const handleSendReset = async () => {
    try {
      await api.post('/admin/reset-senha', { email: resetEmail });
      setShowResetModal(false);
      setResetEmail('');
      alert('Link de reset enviado com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar reset:', error);
      alert('Erro ao enviar link de reset');
    }
  };

  // Novas funções
  const handleImpersonateUser = async (usuario) => {
    try {
      const response = await api.post(`/admin/usuarios/${usuario.id}/impersonar`);
      setImpersonateToken(response.data.token);
      setImpersonatedUser(usuario);
      setShowImpersonateModal(true);
    } catch (error) {
      console.error('Erro ao gerar token de impersonificação:', error);
      alert('Erro ao entrar na conta do usuário');
    }
  };

  const handleChangePasswordUser = (usuario) => {
    setChangingPasswordUser(usuario);
    setNewPassword('');
    setShowChangePasswordModal(true);
  };

  const handleSaveNewPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    try {
      await api.put(`/admin/usuarios/${changingPasswordUser.id}/alterar-senha`, {
        nova_senha: newPassword
      });
      setShowChangePasswordModal(false);
      setChangingPasswordUser(null);
      setNewPassword('');
      alert('Senha alterada com sucesso!');
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      alert('Erro ao alterar senha');
    }
  };

  const handleResendAccess = async (usuario) => {
    if (window.confirm(`Deseja reenviar os acessos para ${usuario.nome}?`)) {
      try {
        const response = await api.post(`/admin/usuarios/${usuario.id}/reenviar-acessos`);
        
        // Mostrar credenciais temporárias (apenas para desenvolvimento)
        if (response.data.credenciais_dev) {
          alert(`Novas credenciais:\nEmail: ${response.data.credenciais_dev.email}\nSenha temporária: ${response.data.credenciais_dev.senha_temporaria}`);
        } else {
          alert('Novas credenciais enviadas por email!');
        }
      } catch (error) {
        console.error('Erro ao reenviar acessos:', error);
        alert('Erro ao reenviar acessos');
      }
    }
  };

  const closeImpersonateModal = () => {
    setShowImpersonateModal(false);
    setImpersonateToken(null);
    setImpersonatedUser(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Gerenciar Usuários</h1>
          <p className="text-slate-300 mt-2">Administre todos os usuários do sistema</p>
        </div>
        <Button 
          onClick={() => setShowResetModal(true)}
          className="bg-green-600 hover:bg-green-700"
        >
          Enviar Reset de Senha
        </Button>
      </div>

      {/* Busca */}
      <Card className="p-4 bg-slate-800/50 border-slate-700/50 backdrop-blur-sm">
        <div className="flex gap-4">
          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={handleSearch}
            className="flex-1 px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-white placeholder-slate-400"
          />
        </div>
      </Card>

      {/* Lista de Usuários */}
      <Card className="overflow-hidden bg-slate-800/50 border-slate-700/50 backdrop-blur-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Usuário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Nível
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Data de Cadastro
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-slate-800/30 divide-y divide-slate-700">
              {usuarios.map((usuario) => (
                <tr key={usuario.id} className="hover:bg-slate-700/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-white">{usuario.nome}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-300">{usuario.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge 
                      variant={usuario.nivel === 'admin' ? 'default' : 'secondary'} 
                      className={usuario.nivel === 'admin' ? 'bg-red-600/20 text-red-400 border-red-600/30' : 'bg-slate-600/20 text-slate-300 border-slate-600/30'}
                    >
                      {usuario.nivel}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                    {formatDate(usuario.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => handleEditUser(usuario)}
                        size="sm"
                        className="bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-600/50"
                      >
                        Editar
                      </Button>
                      
                      <Button
                        onClick={() => handleImpersonateUser(usuario)}
                        size="sm"
                        className="bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 border border-purple-600/50"
                      >
                        Entrar como
                      </Button>
                      
                      <Button
                        onClick={() => handleChangePasswordUser(usuario)}
                        size="sm"
                        className="bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 border border-orange-600/50"
                      >
                        Alterar Senha
                      </Button>
                      
                      <Button
                        onClick={() => handleResendAccess(usuario)}
                        size="sm"
                        className="bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-600/50"
                      >
                        Reenviar Acessos
                      </Button>
                      
                      <Button
                        onClick={() => handleLogoutUser(usuario.id)}
                        size="sm"
                        className="bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 border border-yellow-600/50"
                      >
                        Deslogar
                      </Button>
                      
                      <Button
                        onClick={() => handleDeleteUser(usuario.id)}
                        size="sm"
                        className="bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/50"
                      >
                        Excluir
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex justify-center space-x-2">
          <Button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="bg-slate-700/50 hover:bg-slate-600 text-slate-200 border border-slate-600 disabled:opacity-50"
          >
            Anterior
          </Button>
          
          <span className="flex items-center px-4 py-2 text-slate-300">
            Página {page} de {totalPages}
          </span>
          
          <Button
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
            className="bg-slate-700/50 hover:bg-slate-600 text-slate-200 border border-slate-600 disabled:opacity-50"
          >
            Próxima
          </Button>
        </div>
      )}

      {/* Modal de Edição */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 p-6 rounded-lg w-96 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">Editar Usuário</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Nome</label>
                <input
                  type="text"
                  value={editingUser.nome}
                  onChange={(e) => setEditingUser({...editingUser, nome: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Nível</label>
                <select
                  value={editingUser.nivel}
                  onChange={(e) => setEditingUser({...editingUser, nivel: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="assinante">Assinante</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button onClick={handleSaveUser} className="flex-1 bg-green-600 hover:bg-green-700">
                Salvar
              </Button>
              <Button 
                onClick={() => setEditingUser(null)}
                className="flex-1 bg-slate-700/50 hover:bg-slate-600 text-slate-200 border border-slate-600"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reset */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 p-6 rounded-lg w-96 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">Enviar Reset de Senha</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">Email do usuário</label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Digite o email..."
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex gap-2">
              <Button onClick={handleSendReset} className="flex-1 bg-green-600 hover:bg-green-700">
                Enviar
              </Button>
              <Button 
                onClick={() => setShowResetModal(false)}
                className="flex-1 bg-slate-700/50 hover:bg-slate-600 text-slate-200 border border-slate-600"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Alterar Senha */}
      {showChangePasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 p-6 rounded-lg w-96 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">
              Alterar Senha - {changingPasswordUser?.nome}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">Nova Senha</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Digite a nova senha (mín. 6 caracteres)..."
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex gap-2">
              <Button onClick={handleSaveNewPassword} className="flex-1 bg-orange-600 hover:bg-orange-700">
                Alterar Senha
              </Button>
              <Button 
                onClick={() => setShowChangePasswordModal(false)}
                className="flex-1 bg-slate-700/50 hover:bg-slate-600 text-slate-200 border border-slate-600"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Impersonificação com Iframe */}
      {showImpersonateModal && impersonateToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 p-4 rounded-lg w-[90vw] h-[90vh] border border-slate-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">
                🔄 Navegando como: {impersonatedUser?.nome} ({impersonatedUser?.email})
              </h3>
              <Button 
                onClick={closeImpersonateModal}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                ✕ Fechar
              </Button>
            </div>
            
            <div className="w-full h-[calc(100%-60px)] border border-slate-600 rounded-lg overflow-hidden">
              <ImpersonateFrame token={impersonateToken} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciarUsuarios; 